import { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { STAGE_ORDER_FULL, STAGE_LABELS } from './constants';

const API_URL = import.meta.env.VITE_API_URL;

function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/recruitment/reports`)
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-gray-400 text-sm">Loading reports...</p>;
  if (!data) return <p className="text-gray-400 text-sm">No data yet.</p>;

  const passRateChart = STAGE_ORDER_FULL
    .map((stage) => data.stagePassRates.find((s) => s.stage === stage))
    .filter(Boolean)
    .map((s) => ({ name: STAGE_LABELS[s.stage], passRate: s.passRate, total: s.total }));

  const turnaroundChart = STAGE_ORDER_FULL
    .map((stage) => data.avgStageHours.find((s) => s.stage === stage))
    .filter(Boolean)
    .map((s) => ({ name: STAGE_LABELS[s.stage], hours: s.avgHours }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-800">{data.avgDaysToDecision ?? '—'}</p>
          <p className="text-xs text-gray-500 mt-1">Avg. Days to Decision</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-800">{data.decidedCount}</p>
          <p className="text-xs text-gray-500 mt-1">Decisions Made</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center col-span-2 lg:col-span-1">
          <p className="text-2xl font-bold text-gray-800">{data.byRole.length}</p>
          <p className="text-xs text-gray-500 mt-1">Roles With Applicants</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Pass Rate by Stage</h3>
        {passRateChart.length === 0 ? (
          <p className="text-sm text-gray-400">Not enough data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={passRateChart}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} unit="%" />
              <Tooltip formatter={(v, n, p) => [`${v}% (${p.payload.total} attempts)`, 'Pass rate']} />
              <Bar dataKey="passRate" radius={[4, 4, 0, 0]}>
                {passRateChart.map((entry, i) => (
                  <Cell key={i} fill={entry.passRate >= 60 ? '#10b981' : entry.passRate >= 35 ? '#f59e0b' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Avg. Review Turnaround (Hours)</h3>
        {turnaroundChart.length === 0 ? (
          <p className="text-sm text-gray-400">Not enough data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={turnaroundChart}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} unit="h" />
              <Tooltip formatter={(v) => [`${v}h`, 'Avg. turnaround']} />
              <Bar dataKey="hours" fill="#059669" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Applicants by Role</h3>
        <div className="space-y-2">
          {data.byRole.map((r) => (
            <div key={r.roleKey} className="flex justify-between text-sm">
              <span className="text-gray-600">{r.roleLabel || r.roleKey}</span>
              <span className="font-medium text-gray-800">{r.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Reports;
